import { useRouter } from "next/router";
import type React from "react";
import { useRef } from "react";
import { authPaths, useAuth } from "@medbillai/app/lib/auth";
import { useSignUpOnboardingStore } from "@medbillai/app/lib/sign-up-onboarding/signUpOnboardingStore";
import { anonymousSessionStorage } from "@medbillai/app/lib/storage";
import { usePendingNateraIntake } from "@medbillai/app/screens/natera-intake/pendingActivation";
import { onboardingGate } from "@medbillai/app/screens/onboarding-v3/utils/onboardingGate";
import { FullscreenSpinner } from "@medbillai/ui";
import { buildUrlWithParams, homeUrl, loginUrl, nateraIntakeActivateUrl } from "@medbillai/utils/urls";
export const AuthGate = (props: {
  children: React.ReactNode;
}) => {
  const {
    isReady,
    isAuthenticated,
    setIntendedPath,
    user
  } = useAuth();
  const {
    replace,
    pathname,
    query,
    isReady: routerIsReady
  } = useRouter();
  // The path this instance last redirected away from, not a boolean: every
  // authenticated page roots its layout in an AuthGate, so React keeps one
  // instance across navigations and a one-shot flag would spend itself on the
  // first redirect and leave every later one showing a spinner forever.
  const redirectedFrom = useRef<string | null>(null);
  const {
    setIsSignUpOnboarding
  } = useSignUpOnboardingStore();
  const hasPendingNateraIntake = usePendingNateraIntake({
    skip: !isAuthenticated
  });
  if (!isReady || !routerIsReady) {
    return <FullscreenSpinner />;
  }
  if (!isAuthenticated) {
    if (redirectedFrom.current !== pathname) {
      redirectedFrom.current = pathname;
      const anonToken = typeof query.anonToken === "string" ? query.anonToken : null;
      if (anonToken) {
        void anonymousSessionStorage.set("token", anonToken);
        setIsSignUpOnboarding(true);
      }

      // Only store paths that aren't auth-related and aren't the home page
      const shouldStorePath = pathname !== "/" && !authPaths.includes(pathname);
      if (shouldStorePath) {
        const {
          anonToken: _,
          ...rest
        } = query;
        setIntendedPath(buildUrlWithParams(pathname, rest));
      }
      void replace(loginUrl());
    }
    return <FullscreenSpinner />;
  }
  const resumeUrl = onboardingGate({
    user,
    pathname,
    hasPendingNateraIntake
  });
  if (!resumeUrl) {
    // Arriving somewhere allowed ends the last redirect: the marker guards one
    // navigation, so leaving it set would turn a later visit to that same path
    // — coming back to Home while still pending, say — into a permanent
    // spinner, redirect suppressed and page withheld.
    redirectedFrom.current = null;
  }
  if (resumeUrl) {
    if (redirectedFrom.current !== pathname) {
      redirectedFrom.current = pathname;
      // The gate interrupts the journey, it must not erase it: completion
      // consumes the stash, so a deep link lands where it promised after
      // onboarding instead of at home. Activation never consumes it, so
      // stashing there would only hijack some later navigation.
      const shouldStorePath = pathname !== "/" && !authPaths.includes(pathname) && resumeUrl !== nateraIntakeActivateUrl();
      if (shouldStorePath) {
        setIntendedPath(buildUrlWithParams(pathname, query));
      }
      void replace(resumeUrl);
    }
    return <FullscreenSpinner />;
  }

  // If authenticated and on an auth page, redirect to home
  if (isAuthenticated && authPaths.includes(pathname)) {
    if (redirectedFrom.current !== pathname) {
      redirectedFrom.current = pathname;
      void replace(homeUrl());
    }
    return <FullscreenSpinner />;
  }
  return <>{props.children}</>;
};